import type {
  CurrencyCode,
  CurrencyCodeRecord,
} from 'currency-codes-ts/dist/types';

import currencyCodes, { code, country } from 'currency-codes-ts';

import { currentIntlLocale } from './i18n';

export type { CurrencyCode, CurrencyCodeRecord };

/**
 * Commonly used currencies, sorted by usage frequency.
 * EUR and USD are prioritized as they're the most common.
 */
export const COMMON_CURRENCIES: CurrencyCode[] = ['EUR', 'USD', 'CHF', 'GBP'];
type CurrencyOption = {
  label: string;
  value: CurrencyCode;
};

/**
 * Get localized currency name using Intl.DisplayNames.
 *
 * @param currencyCode - ISO 4217 currency code
 * @param locale - Locale for the currency name. Defaults to the active UI language.
 * @returns Localized currency name
 */
export function getLocalizedCurrencyName(
  currencyCode: string,
  locale: string = currentIntlLocale(),
): string {
  // react-doctor-disable-next-line react-doctor/js-hoist-intl
  const displayNames = new Intl.DisplayNames([locale], { type: 'currency' });

  return displayNames.of(currencyCode) ?? currencyCode;
}

/**
 * Get currency options sorted with common currencies first.
 * Returns options in a format suitable for select components with localized labels.
 *
 * @param locale - Locale for the currency names. Defaults to the active UI language.
 * @param prioritizedCodes - Array of currency codes to show first (defaults to EUR, USD, CHF, GBP)
 * @returns Array of currency options with label and value keys
 */
export function getCurrencyOptions(
  locale: string = currentIntlLocale(),
  prioritizedCodes: CurrencyCode[] = COMMON_CURRENCIES,
) {
  const allCurrencies = currencyCodes.data;
  const prioritizedSet = new Set(prioritizedCodes);
  // react-doctor-disable-next-line react-doctor/js-hoist-intl
  const displayNames = new Intl.DisplayNames([locale], { type: 'currency' });

  const getLabel = (currencyCode: CurrencyCode): string =>
    displayNames.of(currencyCode) ?? currencyCode;

  // Get prioritized currencies in order
  const prioritized: CurrencyOption[] = [];
  for (const currencyCode of prioritizedCodes) {
    // react-doctor-disable-next-line react-doctor/js-index-maps
    const currency = allCurrencies.find((c) => c.code === currencyCode);

    if (!currency) {
      continue;
    }

    prioritized.push({
      label: getLabel(currency.code),
      value: currency.code,
    });
  }

  // Get remaining currencies sorted alphabetically by localized name
  const remaining: CurrencyOption[] = [];
  for (const currency of allCurrencies) {
    if (prioritizedSet.has(currency.code)) {
      continue;
    }

    remaining.push({
      label: getLabel(currency.code),
      value: currency.code,
    });
  }
  remaining.sort((a, b) => a.label.localeCompare(b.label, locale));

  return [...prioritized, ...remaining];
}

/**
 * Get currency name by code.
 *
 * @param currencyCode - ISO 4217 currency code
 * @param locale - Locale for the currency name. Defaults to the active UI language.
 * @returns Currency name or undefined if not found
 */
export function getCurrencyName(
  currencyCode: string,
  locale: string = currentIntlLocale(),
): string | undefined {
  if (!code(currencyCode)) {
    return undefined;
  }

  return getLocalizedCurrencyName(currencyCode, locale);
}

type GetCurrencySymbolOptions<T> = {
  /** Locale for the currency symbol. Defaults to the active UI language. */
  readonly locale?: string;
  /** Fallback for invalid or unformattable codes. Defaults to currencyCode arg. */
  readonly fallbackValue?: T;
};

/**
 * Get currency symbol by currency code.
 *
 * @param currencyCode - ISO 4217 currency code
 * @param options - Locale and fallback value.
 * @returns Currency symbol, or the fallback if the code is invalid or formatting fails
 * @example
 * getCurrencySymbol('EUR') => '€'
 * getCurrencySymbol('USD', { locale: 'en-US' }) => '$'
 * getCurrencySymbol('XYZ', { fallbackValue: '' }) => ''
 */
export function getCurrencySymbol<T = string>(
  currencyCode: string,
  {
    locale = currentIntlLocale(),
    fallbackValue = currencyCode as T,
  }: GetCurrencySymbolOptions<T> = {},
): string | T {
  if (!isValidCurrencyCode(currencyCode)) {
    return fallbackValue;
  }

  try {
    return (
      new Intl.NumberFormat(locale, {
        currency: currencyCode,
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
        style: 'currency',
      })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ?? fallbackValue
    );
  } catch {
    return fallbackValue;
  }
}

/**
 * Get currency record by code.
 *
 * @param currencyCode - ISO 4217 currency code
 * @returns Currency record or undefined if not found
 */
export function getCurrencyByCode(
  currencyCode: string,
): CurrencyCodeRecord | undefined {
  return code(currencyCode);
}

/**
 * Get all currencies used in a specific country.
 *
 * @param countryName - Country name
 * @returns Array of currency records
 */
export function getCurrenciesByCountry(
  countryName: string,
): CurrencyCodeRecord[] {
  return country(countryName);
}

/**
 * Check if a currency code is valid.
 *
 * @param currencyCode - Currency code to validate
 * @returns True if valid ISO 4217 currency code
 */
export function isValidCurrencyCode(
  currencyCode: string,
): currencyCode is CurrencyCode {
  return code(currencyCode) !== undefined;
}
